/**
 * Node Selection Manager
 * Instance-scoped selection manager for LoadSEER nodes
 * Pattern based on Azure provider selection management
 */

import type { NodeSelectionEntry, SelectionResult } from '../types';
import { MAX_NODE_SELECTIONS } from '../constants';

export class NodeSelectionManager {
  private maxSelections: number;
  private selections = new Map<string, NodeSelectionEntry>();

  constructor(maxSelections = MAX_NODE_SELECTIONS) {
    this.maxSelections = maxSelections;
  }

  /**
   * Adds a node to the selection
   * Returns whether the node was added and if the limit was reached
   */
  add(entry: NodeSelectionEntry): SelectionResult {
    // Check if already exists
    if (this.selections.has(entry.lsid)) {
      return { added: false, overLimit: false };
    }

    // Check if over limit
    if (this.selections.size >= this.maxSelections) {
      return { added: false, overLimit: true };
    }

    this.selections.set(entry.lsid, entry);
    return { added: true, overLimit: false };
  }

  /**
   * Removes a node from the selection
   */
  remove(lsid: string): void {
    this.selections.delete(lsid);
  }

  /**
   * Toggles a node selection
   * If selected, removes it. If not selected, adds it.
   */
  toggle(entry: NodeSelectionEntry): SelectionResult {
    if (this.selections.has(entry.lsid)) {
      this.remove(entry.lsid);
      return { added: false, overLimit: false };
    }

    return this.add(entry);
  }

  /**
   * Updates properties of an existing selection
   * Useful for toggling descendant flags without removing/re-adding
   */
  update(lsid: string, updates: Partial<NodeSelectionEntry>): void {
    const existing = this.selections.get(lsid);
    if (!existing) {
      return;
    }

    this.selections.set(lsid, {
      ...existing,
      ...updates,
    });
  }

  /**
   * Clears all selections
   */
  clear(): void {
    this.selections.clear();
  }

  /**
   * Returns an array of all selections
   */
  list(): NodeSelectionEntry[] {
    return Array.from(this.selections.values());
  }

  /**
   * Returns the number of selected nodes
   */
  count(): number {
    return this.selections.size;
  }

  /**
   * Checks if a node is selected
   */
  has(lsid: string): boolean {
    return this.selections.has(lsid);
  }

  /**
   * Gets a specific selection entry
   */
  get(lsid: string): NodeSelectionEntry | undefined {
    return this.selections.get(lsid);
  }

  /**
   * Gets the maximum number of selections allowed
   */
  getMaxSelections(): number {
    return this.maxSelections;
  }

  /**
   * Checks if the selection is at capacity
   */
  isFull(): boolean {
    return this.selections.size >= this.maxSelections;
  }

  /**
   * Gets the number of remaining selection slots
   */
  getRemainingSlots(): number {
    return Math.max(0, this.maxSelections - this.selections.size);
  }
}
